import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Select, message as antMessage, Tag, Tooltip, Spin } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { fetchProfile, removeAssignedExtension } from "../../../core/data/redux/slices/ProfileSlice";
import api from "../../../core/axios/axiosInstance";

const { Option } = Select;

const AssignNumbers = () => {
  const dispatch    = useDispatch();
  const userProfile = useSelector((state) => state.profile);

  const [didNumbers, setDidNumbers] = useState([]);
  const [agents, setAgents]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState({}); // { [key]: true }
  const [addingAgentFor, setAddingAgentFor] = useState(null); // ext.key when showing add-agent dropdown

  // Full list: agents + company admin for assignment dropdowns
  const allAssignableUsers = useMemo(() => {
    if (!userProfile?._id) return agents;
    const adminEntry = {
      _id: userProfile.id,
      firstname: userProfile.firstname || "Admin",
      lastname:  userProfile.lastname  || "",
      email:     userProfile.email     || "",
      isAdmin:   true,
    };
    return [adminEntry, ...agents];
  }, [agents, userProfile]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [numsRes, agentsRes] = await Promise.all([
        api.get("/didlogic/numbers/my?limit=200"),
        api.get("/admin/user/getAgent"),
        dispatch(fetchProfile()), // refresh profile so assignedExtensions is current
      ]);
      setDidNumbers(numsRes.data?.data || []);
      setAgents(agentsRes.data?.data || []);
    } catch {
      antMessage.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Map: telephone number → array of agent objects (supports multi-channel)
  const numberToAgents = useMemo(() => {
    const map = {};
    const add = (tel, agent) => {
      if (!tel) return;
      if (!map[tel]) map[tel] = [];
      if (!map[tel].find((a) => a._id === agent._id)) map[tel].push(agent);
    };
    for (const a of agents) {
      for (const e of (a.assignedExtensions || [])) {
        if (e.PBX_TELEPHONE) add(e.PBX_TELEPHONE, a);
      }
      if (a.PBXDetails?.PBX_TELEPHONE) add(a.PBXDetails.PBX_TELEPHONE, a);
      if (a.telephone) add(a.telephone, a);
      for (const num of (a.assignedCallerNumbers || [])) add(num, a);
    }
    // Include company admin's own assigned caller numbers in the map
    if (userProfile?._id) {
      for (const num of (userProfile.assignedCallerNumbers || [])) {
        add(num, { _id: userProfile.id, firstname: userProfile.firstname || "Admin", lastname: userProfile.lastname || "", email: userProfile.email || "", isAdmin: true });
      }
    }
    return map;
  }, [agents, userProfile]);

  // All visible extensions: admin pool + any extension currently assigned to an agent
  // (regardless of whether it was assigned by superadmin or company admin)
  const extNumbers = useMemo(() => {
    const seen   = new Set(); // dedup by extensionNumber
    const result = [];

    const push = (e) => {
      const key = e.extensionNumber || e.number;
      if (!key || seen.has(key)) return;
      seen.add(key);
      result.push(e);
    };

    // Company admin's own assignedExtensions (their pool)
    for (const e of (userProfile?.assignedExtensions || [])) {
      push({
        key: `ext-${e.extensionNumber}`,
        number: e.PBX_TELEPHONE,
        extensionNumber: e.extensionNumber,
        assignedDeviceId: e.assignedDeviceId || null,
        pbxBaseUrl: e.PBX_BASE_URL || null,
        nickname: e.nickname || "",
        pbxType: e.pbxType || "cloud",
        channels: e.channels || 1,
        isExtension: true,
      });
    }

    // Company admin's primary telephone fallback
    const adminTel = userProfile?.telephone;
    if (adminTel && !(userProfile?.assignedExtensions || []).some((e) => e.PBX_TELEPHONE === adminTel)) {
      push({ key: "ext-primary", number: adminTel, extensionNumber: userProfile?.extensionNumber || "", assignedDeviceId: null, pbxBaseUrl: null, nickname: "", pbxType: "cloud", isExtension: true });
    }

    // Extensions on agents — covers both superadmin-assigned (PBXDetails or assignedExtensions)
    for (const agent of agents) {
      // assignedExtensions array
      for (const e of (agent.assignedExtensions || [])) {
        if (e.extensionNumber && e.PBX_TELEPHONE) {
          push({ key: `ext-${e.extensionNumber}`, number: e.PBX_TELEPHONE, extensionNumber: e.extensionNumber, assignedDeviceId: e.assignedDeviceId || null, pbxBaseUrl: e.PBX_BASE_URL || null, nickname: "", pbxType: e.pbxType || "cloud", channels: e.channels || 1, isExtension: true });
        }
      }
      // PBXDetails primary extension (superadmin-assigned via editCompanyAdminAndAgent)
      if (agent.PBXDetails?.PBX_TELEPHONE && agent.PBXDetails?.PBX_EXTENSION_NUMBER) {
        push({ key: `ext-${agent.PBXDetails.PBX_EXTENSION_NUMBER}`, number: agent.PBXDetails.PBX_TELEPHONE, extensionNumber: agent.PBXDetails.PBX_EXTENSION_NUMBER, assignedDeviceId: agent.PBXDetails.assignedDeviceId || null, pbxBaseUrl: agent.PBXDetails.PBX_BASE_URL || null, nickname: "", pbxType: "cloud", isExtension: true });
      }
      // top-level telephone (legacy)
      if (agent.telephone && agent.extensionNumber) {
        push({ key: `ext-${agent.extensionNumber}`, number: agent.telephone, extensionNumber: agent.extensionNumber, assignedDeviceId: agent.assignedDeviceId || null, pbxBaseUrl: null, nickname: "", pbxType: "cloud", isExtension: true });
      }
    }

    return result;
  }, [userProfile, agents]);

  // Assign/unassign a DID (uses DIDAssignment _id)
  const handleAssignDID = async (record, agentId) => {
    const key = record._id;
    setSaving((prev) => ({ ...prev, [key]: true }));
    try {
      await api.put(`/didlogic/numbers/my/${record._id}/assign`, { agentId: agentId || null });
      antMessage.success(agentId ? "Number assigned to agent" : "Number returned to your pool");
      const agent = agentId ? agents.find((a) => a._id === agentId) : null;
      setDidNumbers((prev) =>
        prev.map((n) =>
          n._id === record._id
            ? { ...n, assignedAgentId: agent ? { _id: agent._id, firstname: agent.firstname, lastname: agent.lastname } : null }
            : n
        )
      );
      // Refresh agents so numberToAgent map updates
      const agentsRes = await api.get("/admin/user/getAgent");
      setAgents(agentsRes.data?.data || []);
    } catch {
      antMessage.error("Failed to update assignment");
    } finally {
      setSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleAssignExt = async (ext, currentAgentId, newAgentId, saveKeyOverride) => {
    const key = saveKeyOverride || `ext-${ext.number}`;
    setSaving((prev) => ({ ...prev, [key]: true }));
    const payload = {
      fromAgentId: currentAgentId || null,
      toAgentId:   newAgentId   || null,
      extension: {
        extensionNumber:  ext.extensionNumber,
        PBX_TELEPHONE:    ext.number,
        assignedDeviceId: ext.assignedDeviceId || null,
        pbxType:          ext.pbxType || "cloud",
        PBX_BASE_URL:     ext.pbxBaseUrl || null,
        channels:         ext.channels  || 1,
      },
    };
    console.log("[AssignExt] REQUEST", JSON.stringify(payload));
    try {
      const res = await api.put("/admin/user/reassign-extension", payload);
      console.log("[AssignExt] RESPONSE", res.status, JSON.stringify(res.data));
      antMessage.success(newAgentId ? "Extension assigned to agent" : "Extension removed from pool");
      // Optimistic update: admin removing themselves from a multichannel ext only
      const isMultiChannelExt = (ext.channels || 1) > 1;
      const isAdminSelfRemoval = isMultiChannelExt && !newAgentId && currentAgentId && userProfile?._id &&
        String(currentAgentId) === String(userProfile.id);
      console.log("[AssignExt] isMultiChannelExt:", isMultiChannelExt, "isAdminSelfRemoval:", isAdminSelfRemoval);
      if (isAdminSelfRemoval) {
        dispatch(removeAssignedExtension({ telephone: ext.number, extensionNumber: ext.extensionNumber }));
        console.log("[AssignExt] optimistic remove dispatched");
      }
      // Refresh profile first, then agents — ensures Redux state is committed before re-render
      const profileResult = await dispatch(fetchProfile());
      console.log("[AssignExt] profile after refresh — assignedExtensions:", JSON.stringify(profileResult?.payload?.data?.assignedExtensions));
      const agentsRes = await api.get("/admin/user/getAgent");
      setAgents(agentsRes.data?.data || []);
    } catch (err) {
      console.error("[AssignExt] ERROR", err?.response?.status, JSON.stringify(err?.response?.data), err?.message);
      antMessage.error(err?.response?.data?.message || "Failed to update assignment");
    } finally {
      setSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  const agentSelectOptions = allAssignableUsers.map((a) => (
    <Option key={a._id} value={a._id}>
      <span>{a.firstname} {a.lastname}{a.isAdmin ? " (Admin)" : ""}</span>
      <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 6 }}>{a.email}</span>
    </Option>
  ));

  const renderAssignSelect = (currentAgentId, onChange, saveKey) => (
    saving[saveKey] ? <Spin size="small" /> : (
      <Select
        value={currentAgentId || ""}
        onChange={onChange}
        style={{ width: "100%" }}
        size="small"
        allowClear
        onClear={() => onChange("")}
      >
        <Option value="">
          <span style={{ color: "#6b7280", fontStyle: "italic" }}>— Your pool —</span>
        </Option>
        {agentSelectOptions}
      </Select>
    )
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header mb-3">
          <div className="row align-items-center">
            <div className="col">
              <h4 className="page-title mb-0">Assign Phone Numbers</h4>
              <p className="text-muted small mb-0">
                Assign numbers to agents. A purchased DID assigned to an agent is removed from your calling pool — reassign or return it to regain access. Extensions can be shared or reassigned freely.
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 48 }}><Spin size="large" /></div>
        ) : (
          <>
            {/* ── Phone Numbers (extensions + purchased DIDs) ──────────── */}
            {(extNumbers.length > 0 || didNumbers.length > 0) && (
              <div className="card mb-3">
                <div className="card-header py-2 px-3" style={{ background: "#f8fafc" }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: "#374151" }}>Phone Numbers</span>
                  <span style={{ fontSize: 12, color: "#9ca3af", marginLeft: 8 }}>Assign extensions and purchased numbers to agents or your pool</span>
                </div>
                <div className="card-body p-0">
                  <div style={{ overflowX: "auto" }}>
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr style={{ background: "#f9fafb" }}>
                        <th style={thStyle}>Ext No.</th>
                        <th style={thStyle}>Telephone</th>
                        <th style={thStyle}>Status</th>
                        <th style={thStyle}>Assigned To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {extNumbers.map((ext) => {
                        const assignedAgents = numberToAgents[ext.number] || [];
                        const channels = ext.channels || 1;
                        const isMultiChannel = channels > 1;
                        const adminHasExt = (userProfile?.assignedExtensions || []).some(
                          (e) => (e.extensionNumber === ext.extensionNumber || e.PBX_TELEPHONE === ext.number) && e.inAdminPool !== false
                        );
                        const usedChannels = assignedAgents.length + (adminHasExt ? 1 : 0);

                        return (
                          <tr key={ext.key}>
                            <td style={tdStyle}>
                              <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#374151" }}>{ext.extensionNumber || "—"}</span>
                            </td>
                            <td style={tdStyle}>
                              <span style={{ fontFamily: "monospace", fontSize: 13, color: "#111827" }}>{ext.number}</span>
                            </td>
                            <td style={tdStyle}>
                              {isMultiChannel ? (
                                <Tag color="purple" style={{ fontSize: 11 }}>Multi-channel</Tag>
                              ) : assignedAgents.length > 0 ? (
                                <Tag color="blue" style={{ fontSize: 11 }}>Agent</Tag>
                              ) : (
                                <Tag color="green" style={{ fontSize: 11 }}>Your Pool</Tag>
                              )}
                            </td>
                            <td style={{ ...tdStyle, minWidth: 300 }}>
                              {isMultiChannel ? (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                                  {(userProfile?.assignedExtensions || []).some(
                                    (e) => (e.extensionNumber === ext.extensionNumber || e.PBX_TELEPHONE === ext.number) && e.inAdminPool !== false
                                  ) && (
                                    saving[`ext-remove-${ext.number}-${userProfile.id}`] ? (
                                      <Spin size="small" key="admin" />
                                    ) : (
                                      <Tag
                                        key="admin"
                                        color="purple"
                                        closable
                                        style={{ fontSize: 11, margin: 0 }}
                                        onClose={(e) => {
                                          e.preventDefault();
                                          handleAssignExt(ext, userProfile.id, null, `ext-remove-${ext.number}-${userProfile.id}`);
                                        }}
                                      >
                                        — Your pool —
                                      </Tag>
                                    )
                                  )}
                                  {assignedAgents.map((a) => (
                                    saving[`ext-remove-${ext.number}-${a._id}`] ? (
                                      <Spin size="small" key={a._id} />
                                    ) : (
                                      <Tag
                                        key={a._id}
                                        color="blue"
                                        closable
                                        style={{ fontSize: 11, margin: 0 }}
                                        onClose={(e) => { e.preventDefault(); handleAssignExt(ext, a._id, null, `ext-remove-${ext.number}-${a._id}`); }}
                                      >
                                        {a.firstname} {a.lastname}
                                      </Tag>
                                    )
                                  ))}
                                  <span style={{ fontSize: 11, color: usedChannels >= channels ? "#ef4444" : "#6b7280", marginLeft: 2 }}>
                                    {usedChannels}/{channels} ch
                                  </span>
                                  {usedChannels < channels && (
                                    addingAgentFor === ext.key ? (
                                      saving[`ext-${ext.number}`] ? <Spin size="small" /> : (
                                        <Select
                                          value={undefined}
                                          onChange={(newAgentId) => {
                                            setAddingAgentFor(null);
                                            const targetId = newAgentId === "__pool__" ? userProfile.id : newAgentId;
                                            if (!targetId) return;
                                            handleAssignExt(ext, null, targetId);
                                          }}
                                          style={{ width: 180 }}
                                          size="small"
                                          autoFocus
                                          onBlur={() => setAddingAgentFor(null)}
                                          placeholder="Select agent…"
                                        >
                                          <Option value="__pool__">
                                            <span style={{ color: "#6b7280", fontStyle: "italic" }}>— Your pool —</span>
                                          </Option>
                                          {allAssignableUsers
                                            .filter((a) => {
                                              if (assignedAgents.find((aa) => aa._id === a._id)) return false;
                                              if (a.isAdmin) {
                                                return !(userProfile?.assignedExtensions || []).some(
                                                  (e) => (e.extensionNumber === ext.extensionNumber || e.PBX_TELEPHONE === ext.number) && e.inAdminPool !== false
                                                );
                                              }
                                              return true;
                                            })
                                            .map((a) => (
                                              <Option key={a._id} value={a._id}>
                                                {a.firstname} {a.lastname}{a.isAdmin ? " (Admin)" : ""}
                                              </Option>
                                            ))}
                                        </Select>
                                      )
                                    ) : (
                                      <button
                                        onClick={() => setAddingAgentFor(ext.key)}
                                        style={{ background: "none", border: "1px dashed #d1d5db", borderRadius: 4, cursor: "pointer", padding: "2px 8px", fontSize: 11, color: "#6b7280" }}
                                      >
                                        + Add Agent
                                      </button>
                                    )
                                  )}
                                </div>
                              ) : (
                                renderAssignSelect(
                                  assignedAgents[0]?._id || "",
                                  (newAgentId) => handleAssignExt(ext, assignedAgents[0]?._id || null, newAgentId || null),
                                  `ext-${ext.number}`
                                )
                              )}
                            </td>
                          </tr>
                        );
                      })}

                      {didNumbers.filter((num) => !extNumbers.some((e) => e.number === num.number)).map((num) => {
                        const currentAgentId = num.assignedAgentId?._id || num.assignedAgentId || "";
                        return (
                          <tr key={num._id}>
                            <td style={tdStyle}>
                              <span style={{ color: "#9ca3af", fontFamily: "monospace" }}>—</span>
                              {num.label && <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 6 }}>{num.label}</span>}
                            </td>
                            <td style={tdStyle}>
                              <span style={{ fontFamily: "monospace", fontSize: 13, color: "#111827" }}>{num.number}</span>
                            </td>
                            <td style={tdStyle}>
                              <Tag color="geekblue" style={{ fontSize: 11 }}>Purchased</Tag>
                            </td>
                            <td style={{ ...tdStyle, minWidth: 300 }}>
                              {saving[num._id] ? <Spin size="small" /> : (
                                <Select
                                  value={currentAgentId}
                                  onChange={(val) => handleAssignDID(num, val || null)}
                                  style={{ width: "100%" }}
                                  size="small"
                                  allowClear
                                  onClear={() => handleAssignDID(num, null)}
                                  placeholder="— Your pool —"
                                >
                                  <Option value="">
                                    <span style={{ color: "#6b7280", fontStyle: "italic" }}>— Your pool —</span>
                                  </Option>
                                  {allAssignableUsers.map((a) => (
                                    <Option key={a._id} value={a._id}>
                                      <span>{a.firstname} {a.lastname}{a.isAdmin ? " (Admin)" : ""}</span>
                                      <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 6 }}>{a.email}</span>
                                    </Option>
                                  ))}
                                </Select>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </div>
              </div>
            )}

          </>
        )}
      </div>
    </div>
  );
};

const thStyle = { padding: "10px 16px", fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" };
const tdStyle = { padding: "10px 16px", verticalAlign: "middle" };

export default AssignNumbers;
